import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Select,
  SelectItem,
  Input,
  Chip,
  Card,
  CardBody,
  Autocomplete,
  AutocompleteItem,
  Switch,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import {
  MappingSuggestion,
  FieldMapping,
  getFieldLabel,
} from '../../utils/column-matcher';
import {
  requiredFields,
  shipmentIdentifierFields,
  optionalFields,
  multiPackingField,
} from '../../utils/shipments-excel-mapping';
import { Icon } from '@iconify/react';

interface ColumnMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  exporterColumns: string[];
  sampleData: Record<string, unknown>[];
  initialSuggestions: MappingSuggestion[];
  onConfirm: (
    mapping: FieldMapping,
    profileName: string | null,
    packingName: string | null,
    dateFormats: Record<string, string>,
    isMultiPacking: boolean
  ) => void;
  profileName?: string;
  packingName?: string;
  availableSheets?: string[];
  selectedSheet?: string;
  onSheetChange?: (sheet: string) => void;
  headerRow?: number;
  onHeaderRowChange?: (row: number) => void;
  showSheetSelector?: boolean;
  initialDateFormats?: Record<string, string>;
}

const ColumnMappingModal = ({
  isOpen,
  onClose,
  exporterColumns,
  sampleData,
  initialSuggestions,
  onConfirm,
  profileName: initialProfileName,
  packingName: initialPackingName,
  availableSheets = [],
  selectedSheet = '',
  onSheetChange,
  headerRow = 1,
  onHeaderRowChange,
  showSheetSelector = false,
  initialDateFormats,
}: ColumnMappingModalProps) => {
  // Current mapping state (system field -> excel column)
  const [currentMapping, setCurrentMapping] = useState<FieldMapping>({});
  const [profileName, setProfileName] = useState(initialProfileName || '');
  const [packingName, setPackingName] = useState(initialPackingName || '');
  const [isMultiPacking, setIsMultiPacking] = useState(false);
  const [dateFormat, setDateFormat] = useState<string>(
    initialDateFormats?.default || ''
  );

  // System fields (required and optional) — includes packing_name when multi-packing
  const systemFields = useMemo(() => {
    const fields = isMultiPacking
      ? [
          multiPackingField,
          ...requiredFields,
          ...shipmentIdentifierFields,
          ...optionalFields,
        ]
      : [...requiredFields, ...shipmentIdentifierFields, ...optionalFields];
    return fields;
  }, [isMultiPacking]);

  // Initialize mapping from suggestions (convert to system -> excel format)
  useEffect(() => {
    if (isOpen) {
      const initialMapping: FieldMapping = {};

      // Group suggestions by system field to handle conflicts
      const fieldToSuggestions = new Map<string, typeof initialSuggestions>();

      initialSuggestions.forEach((suggestion) => {
        if (suggestion.suggestedField) {
          if (!fieldToSuggestions.has(suggestion.suggestedField)) {
            fieldToSuggestions.set(suggestion.suggestedField, []);
          }
          fieldToSuggestions.get(suggestion.suggestedField)!.push(suggestion);
        }
      });

      // For each system field, pick the BEST Excel column match
      fieldToSuggestions.forEach((suggestions, systemField) => {
        if (suggestions.length === 1) {
          // Only one match, use it
          initialMapping[systemField] = suggestions[0].exporterColumn;
        } else {
          // Multiple Excel columns match this field - pick the best one
          // Prioritize: high confidence > medium > low
          // If same confidence, prefer the one that matches the Spanish field name
          const systemFieldSpanishName =
            getFieldLabel(systemField).toLowerCase();

          // Extract key words from Spanish name (ignore common words)
          const spanishWords = systemFieldSpanishName
            .split(' ')
            .filter((w) => w.length > 3 && !['para', 'con', 'sin'].includes(w));

          const best = suggestions.reduce((best, current) => {
            const confidenceOrder = { high: 3, medium: 2, low: 1, none: 0 };
            const bestConf = confidenceOrder[best.confidence];
            const currConf = confidenceOrder[current.confidence];

            if (currConf > bestConf) return current;
            if (currConf < bestConf) return best;

            // Same confidence - prefer match that contains key Spanish words
            const bestLower = best.exporterColumn.toLowerCase();
            const currLower = current.exporterColumn.toLowerCase();

            // Check if Excel column contains any key Spanish word
            const bestMatchScore = spanishWords.filter((word) =>
              bestLower.includes(word)
            ).length;
            const currMatchScore = spanishWords.filter((word) =>
              currLower.includes(word)
            ).length;

            if (currMatchScore > bestMatchScore) return current;
            if (bestMatchScore > currMatchScore) return best;

            // If still tied, prefer exact match to full Spanish name
            const bestExact = bestLower === systemFieldSpanishName;
            const currExact = currLower === systemFieldSpanishName;

            if (currExact && !bestExact) return current;
            if (bestExact && !currExact) return best;

            // Otherwise keep the first one
            return best;
          });

          initialMapping[systemField] = best.exporterColumn;
        }
      });

      setCurrentMapping(initialMapping);

      // Set initial profile name and packing name if provided
      if (initialProfileName) {
        setProfileName(initialProfileName);
      }
      if (initialPackingName) {
        setPackingName(initialPackingName);
      }
    }
  }, [isOpen, initialSuggestions, initialProfileName, initialPackingName]);

  // Validation - check if all required system fields are mapped
  const validation = useMemo(() => {
    const mappedFields = Object.keys(currentMapping);
    const missingFieldDefs = systemFields.filter(
      (fieldDef) =>
        fieldDef.required &&
        (!mappedFields.includes(fieldDef.field) ||
          !currentMapping[fieldDef.field])
    );

    const hasTransportIdentifier = shipmentIdentifierFields.some((fieldDef) =>
      Boolean(currentMapping[fieldDef.field])
    );
    const missingFields = hasTransportIdentifier
      ? missingFieldDefs
      : [
          ...missingFieldDefs,
          {
            field: 'container_or_awb',
            label: 'Número de Contenedor o AWB',
            helperText: 'Mapea al menos uno de los dos identificadores',
            required: true,
            shipmentField: true,
          },
        ];

    return {
      valid: missingFields.length === 0,
      missingFields,
    };
  }, [currentMapping, systemFields]);

  // Get AI suggestion for a system field (currently unused but kept for future use)
  // const getSuggestionForSystemField = (systemField: string): string | null => {
  //   // Find which exporter column was suggested for this system field
  //   const suggestion = initialSuggestions.find(
  //     (s) => s.suggestedField === systemField
  //   );
  //   return suggestion?.exporterColumn || null;
  // };

  // Handle field mapping change (system field -> excel column)
  const handleMappingChange = (systemField: string, excelColumn: string) => {
    setCurrentMapping((prev) => ({
      ...prev,
      [systemField]: excelColumn,
    }));
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!validation.valid) {
      toast.error(
        `Faltan campos obligatorios: ${validation.missingFields.map((f) => f.label).join(', ')}`
      );
      return;
    }

    if (!profileName.trim()) {
      toast.error('Por favor ingresa un nombre para la planilla');
      return;
    }

    if (!isMultiPacking && !packingName.trim()) {
      toast.error('Por favor ingresa el nombre de la planta de empaque');
      return;
    }

    if (isMultiPacking && !currentMapping['packing_name']) {
      toast.error('Por favor mapea la columna de Packing de tu Excel');
      return;
    }

    if (!dateFormat) {
      toast.error('Por favor selecciona el formato de fechas');
      return;
    }

    onConfirm(
      currentMapping,
      profileName.trim(),
      isMultiPacking ? null : packingName.trim(),
      { default: dateFormat },
      isMultiPacking
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='5xl'
      scrollBehavior='inside'
      isDismissable={false}
    >
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          <h3 className='text-xl font-semibold'>Mapeo de Columnas</h3>
          <p className='text-sm text-default-500 font-normal'>
            Asocia las columnas de tu archivo Excel con los campos del sistema
          </p>
        </ModalHeader>

        <ModalBody>
          <div className='flex flex-col gap-4'>
            {/* Sheet and Header Row Selector - shown at top if needed */}
            {showSheetSelector && (
              <Card className='bg-orange-50 border-orange-200'>
                <CardBody>
                  <h4 className='text-sm font-semibold text-orange-900 mb-3'>
                    Configurar archivo Excel
                  </h4>
                  <div className='grid grid-cols-2 gap-4'>
                    {/* Sheet Selector */}
                    {availableSheets.length > 1 && (
                      <Select
                        label='Seleccionar Hoja'
                        placeholder='Seleccione una hoja'
                        selectedKeys={selectedSheet ? [selectedSheet] : []}
                        onChange={(e) => onSheetChange?.(e.target.value)}
                        size='sm'
                      >
                        {availableSheets.map((sheet) => (
                          <SelectItem key={sheet} value={sheet}>
                            {sheet}
                          </SelectItem>
                        ))}
                      </Select>
                    )}

                    {/* Header Row Selector */}
                    <Input
                      type='number'
                      label='Fila de Encabezados'
                      placeholder='Ej: 1'
                      value={String(headerRow)}
                      onValueChange={(val) =>
                        onHeaderRowChange?.(Number(val) || 1)
                      }
                      min={1}
                      max={100}
                      size='sm'
                      description='¿En qué fila están los nombres de columnas?'
                    />
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Save Profile Section - Always Required */}
            <Card>
              <CardBody>
                <div className='flex flex-col gap-4'>
                  <h4 className='text-sm font-semibold text-default-600'>
                    Guardar Planilla
                  </h4>
                  <p className='text-xs text-default-500'>
                    Esta configuración se guardará para futuros usos
                  </p>
                  <div className='flex items-start gap-3 p-3 rounded-lg bg-default-50'>
                    <Switch
                      size='sm'
                      isSelected={isMultiPacking}
                      onValueChange={setIsMultiPacking}
                    />
                    <div className='flex flex-col gap-1'>
                      <span className='text-sm font-medium'>
                        Planilla Múltiples Plantas
                      </span>
                      <span className='text-xs text-default-400'>
                        Deberás tener una columna adicional Planta en tu Excel
                        para identificar de qué planta viene.
                      </span>
                    </div>
                  </div>
                  <div className='grid grid-cols-2 gap-4'>
                    <Input
                      size='sm'
                      label='Nombre de la Planilla'
                      placeholder='Ej: Planilla Planta San Juan versión 1.0'
                      value={profileName}
                      onValueChange={setProfileName}
                      startContent={<Icon icon='tabler:device-floppy' />}
                      isRequired
                    />
                    {!isMultiPacking && (
                      <Input
                        size='sm'
                        label='Nombre de la Planta'
                        placeholder='Ej: Planta San Juan'
                        value={packingName}
                        onValueChange={setPackingName}
                        isRequired
                      />
                    )}
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <Select
                      size='sm'
                      label='¿En qué formato vienen las fechas?'
                      placeholder='Selecciona el formato de fechas'
                      selectedKeys={dateFormat ? [dateFormat] : []}
                      onChange={(e) => setDateFormat(e.target.value)}
                      isRequired
                      description='Se aplicará a las fechas (ETD, ETA, etc.)'
                    >
                      {[
                        'dd-MM-yy',
                        'MM-dd-yy',
                        'yy-MM-dd',
                        'dd-MM-yyyy',
                        'yyyy-MM-dd',
                        'MM-dd-yyyy',
                      ].map((fmt) => (
                        <SelectItem key={fmt} value={fmt}>
                          {fmt}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </div>
              </CardBody>
            </Card>
            {/* Validation Summary */}
            <Card
              className={
                validation.valid
                  ? 'bg-green-50 dark:bg-green-950'
                  : 'bg-yellow-50 dark:bg-yellow-950'
              }
            >
              <CardBody>
                <div className='flex items-center gap-2'>
                  {validation.valid ? (
                    <>
                      <Icon icon='tabler:circle-check' className='text-green-600' />
                      <span className='text-green-700 font-medium'>
                        Todos los campos obligatorios están mapeados
                      </span>
                    </>
                  ) : (
                    <>
                      <Icon icon='tabler:alert-triangle' className='text-yellow-600' />
                      <div className='flex flex-col'>
                        <span className='text-yellow-700 font-medium'>
                          Faltan campos obligatorios:
                        </span>
                        <span className='text-sm text-yellow-600'>
                          {validation.missingFields
                            .map((f) => f.label)
                            .join(', ')}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Mapping Table */}
            <Table aria-label='Tabla de mapeo de columnas'>
              <TableHeader>
                <TableColumn>Campo del Sistema</TableColumn>
                {/* <TableColumn>Sugerencia IA</TableColumn> */}
                <TableColumn>Columna de Excel</TableColumn>
                <TableColumn>Vista Previa</TableColumn>
              </TableHeader>
              <TableBody>
                {systemFields.map((fieldDef) => {
                  const selectedExcelColumn = currentMapping[fieldDef.field];
                  const sampleValue = selectedExcelColumn
                    ? sampleData[0]?.[selectedExcelColumn]
                    : null;

                  return (
                    <TableRow key={fieldDef.field}>
                      {/* System Field */}
                      <TableCell>
                        <div className='flex flex-col gap-1'>
                          <div className='flex items-center gap-2'>
                            <span className='font-medium'>
                              {fieldDef.label}
                            </span>
                            {fieldDef.required && (
                              <Chip size='sm' color='danger' variant='flat'>
                                Requerido
                              </Chip>
                            )}
                          </div>
                          {fieldDef.helperText && (
                            <span className='text-xs text-default-400'>
                              {fieldDef.helperText}
                            </span>
                          )}
                        </div>
                      </TableCell>

                      {/* Excel Column Selector (searchable) */}
                      <TableCell>
                        <Autocomplete
                          size='sm'
                          placeholder='Seleccionar columna'
                          aria-label={`Seleccionar columna para ${fieldDef.label}`}
                          selectedKey={selectedExcelColumn ?? null}
                          onSelectionChange={(key) => {
                            if (key != null) {
                              handleMappingChange(fieldDef.field, String(key));
                            } else {
                              // Clear selection: remove mapping for this field
                              setCurrentMapping((prev) => {
                                const { [fieldDef.field]: _removed, ...rest } =
                                  prev;
                                return rest;
                              });
                            }
                          }}
                          allowsCustomValue={false}
                          defaultItems={exporterColumns
                            .slice()
                            .sort()
                            .map((c) => ({ id: c, label: c }))}
                          className='min-w-[260px]'
                          isRequired={fieldDef.required}
                        >
                          {(item) => (
                            <AutocompleteItem key={item.id} value={item.id}>
                              {item.label}
                            </AutocompleteItem>
                          )}
                        </Autocomplete>
                      </TableCell>

                      {/* Sample Data Preview */}
                      <TableCell>
                        <span className='text-xs text-default-500 italic'>
                          {sampleValue ? String(sampleValue) : '-'}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {/* Validation Summary */}
            <Card
              className={
                validation.valid
                  ? 'bg-green-50 dark:bg-green-950'
                  : 'bg-yellow-50 dark:bg-yellow-950'
              }
            >
              <CardBody>
                <div className='flex items-center gap-2'>
                  {validation.valid ? (
                    <>
                      <Icon icon='tabler:circle-check' className='text-green-600' />
                      <span className='text-green-700 font-medium'>
                        Todos los campos obligatorios están mapeados
                      </span>
                    </>
                  ) : (
                    <>
                      <Icon icon='tabler:alert-triangle' className='text-yellow-600' />
                      <div className='flex flex-col'>
                        <span className='text-yellow-700 font-medium'>
                          Faltan campos obligatorios:
                        </span>
                        <span className='text-sm text-yellow-600'>
                          {validation.missingFields
                            .map((f) => f.label)
                            .join(', ')}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </CardBody>
            </Card>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant='light' onPress={onClose}>
            Cancelar
          </Button>
          <Button
            color='primary'
            onPress={handleConfirm}
            isDisabled={
              !validation.valid ||
              !profileName.trim() ||
              (!isMultiPacking && !packingName.trim())
            }
          >
            Guardar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ColumnMappingModal;
